<template>
  <div class="payment-method-selector">
    <h3 class="selector-title">Select Payment Method</h3>
    <p class="selector-subtitle">Choose how you'd like to pay for your subscription</p>

    <div class="payment-methods-grid">
      <!-- DPO - Pay Online Now (Hidden temporarily) -->
      <button
        v-if="false"
        type="button"
        class="payment-card payment-card--featured"
        :class="{ 'is-loading': isLoading && loadingMethod === 'DPO' }"
        :disabled="isLoading"
        @click="selectMethod('DPO')"
      >
        <div v-if="lastPaymentMethod === 'DPO'" class="continue-badge">Continue</div>
        <div class="discount-badge">{{ discountBadgeText }}</div>
        <div class="card-icon card-icon--dpo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
            <line x1="1" y1="10" x2="23" y2="10"></line>
          </svg>
        </div>
        <div class="card-content">
          <span class="card-title">Pay Online Now</span>
          <span class="card-description">Pay securely with card or mobile money</span>
          <span v-if="amount" class="card-price">
            <span class="price-discounted">{{ formatMoney(discountedAmount) }}</span>
            <span class="price-original">{{ formatMoney(amount) }}</span>
          </span>
        </div>
        <div class="card-arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </div>
        <div v-if="isLoading && loadingMethod === 'DPO'" class="card-loading">
          <span class="spinner"></span>
        </div>
      </button>

      <!-- EFT / Bank Transfer -->
      <button
        type="button"
        class="payment-card"
        :class="{ 'is-loading': isLoading && loadingMethod === 'EFT', 'payment-card--last-used': lastPaymentMethod === 'EFT' }"
        :disabled="isLoading"
        @click="selectMethod('EFT')"
      >
        <div v-if="lastPaymentMethod === 'EFT'" class="continue-badge continue-badge--secondary">Continue</div>
        <div class="card-icon">
          <img src="@/assets/classic.jpg" alt="Bank" />
        </div>
        <div class="card-content">
          <span class="card-title">Bank / EFT</span>
          <span class="card-description">Pay via bank transfer using the reference provided</span>
        </div>
        <div class="card-arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </div>
        <div v-if="isLoading && loadingMethod === 'EFT'" class="card-loading">
          <span class="spinner"></span>
        </div>
      </button>

      <!-- Mobile Money -->
      <button
        type="button"
        class="payment-card"
        :class="{ 'is-loading': isLoading && loadingMethod === 'Kazang', 'payment-card--last-used': lastPaymentMethod === 'Kazang' }"
        :disabled="isLoading"
        @click="selectMethod('Kazang')"
      >
        <div v-if="lastPaymentMethod === 'Kazang'" class="continue-badge continue-badge--secondary">Continue</div>
        <div class="card-icon">
          <img src="@/assets/Kazang.png" alt="Kazang" />
        </div>
        <div class="card-content">
          <span class="card-title">Mobile Money</span>
          <span class="card-description">Pay instantly with MTN, Airtel, or Zamtel</span>
        </div>
        <div class="card-arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </div>
        <div v-if="isLoading && loadingMethod === 'Kazang'" class="card-loading">
          <span class="spinner"></span>
        </div>
      </button>

      <!-- Kazang Vendor Deposit -->
      <button
        type="button"
        class="payment-card"
        :class="{ 'is-loading': isLoading && loadingMethod === 'Kazang VCD', 'payment-card--last-used': lastPaymentMethod === 'Kazang VCD' }"
        :disabled="isLoading"
        @click="selectMethod('Kazang VCD')"
      >
        <div v-if="lastPaymentMethod === 'Kazang VCD'" class="continue-badge continue-badge--secondary">Continue</div>
        <div class="card-icon">
          <img src="@/assets/Kazang.png" alt="Kazang VCD" />
        </div>
        <div class="card-content">
          <span class="card-title">Kazang Vendor Deposit</span>
          <span class="card-description">Use your Kazang vendor account balance</span>
        </div>
        <div class="card-arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </div>
        <div v-if="isLoading && loadingMethod === 'Kazang VCD'" class="card-loading">
          <span class="spinner"></span>
        </div>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaymentMethodSelector',
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    loadingMethod: {
      type: String,
      default: null
    },
    amount: {
      type: Number,
      default: null
    },
    lastPaymentMethod: {
      type: String,
      default: null
    }
  },
  computed: {
    discountRate() {
      return typeof window.DPO_DISCOUNT === 'number' ? window.DPO_DISCOUNT : 0
    },
    discountPercentage() {
      return Number((this.discountRate * 100).toFixed(2))
    },
    discountBadgeText() {
      return `${this.discountPercentage}% OFF`
    },
    discountAmount() {
      if (!this.amount) return 0
      return Math.round((this.amount * this.discountRate) * 100) / 100
    },
    discountedAmount() {
      if (!this.amount) return 0
      return Math.round((this.amount - this.discountAmount) * 100) / 100
    }
  },
  methods: {
    selectMethod(method) {
      if (!this.isLoading) {
        this.$emit('select-method', method)
      }
    },
    formatMoney(value) {
      if (value == null) return ''
      return 'K ' + Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }
  }
}
</script>

<style scoped>
.payment-method-selector {
  margin-top: 1.5rem;
}

.selector-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #18181b;
  margin: 0 0 0.25rem 0;
}

.selector-subtitle {
  font-size: 0.875rem;
  color: #71717a;
  margin: 0 0 1.25rem 0;
}

.payment-methods-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.payment-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 1rem 1.25rem;
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  position: relative;
  overflow: hidden;
}

.payment-card:hover:not(:disabled) {
  border-color: #a1a1aa;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.payment-card:active:not(:disabled) {
  transform: translateY(0);
}

.payment-card:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.payment-card.is-loading {
  pointer-events: none;
}

/* Featured DPO Card Styles */
.payment-card--featured {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  border-color: #059669;
  color: #ffffff;
  position: relative;
}

.payment-card--featured:hover:not(:disabled) {
  border-color: #047857;
  box-shadow: 0 8px 16px -4px rgba(5, 150, 105, 0.4);
}

.payment-card--featured .card-title {
  color: #ffffff;
}

.payment-card--featured .card-description {
  color: rgba(255, 255, 255, 0.85);
}

.payment-card--featured .card-arrow {
  color: rgba(255, 255, 255, 0.7);
}

.payment-card--featured:hover:not(:disabled) .card-arrow {
  color: #ffffff;
}

.discount-badge {
  position: absolute;
  top: -1px;
  right: 12px;
  background: #fbbf24;
  color: #78350f;
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.25rem 0.5rem;
  border-radius: 0 0 0.375rem 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.continue-badge {
  position: absolute;
  top: -1px;
  left: 12px;
  background: rgba(255, 255, 255, 0.25);
  color: #ffffff;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 0 0 0.375rem 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.continue-badge--secondary {
  background: #3b82f6;
  color: #ffffff;
}

/* Last used card highlight */
.payment-card--last-used {
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px #3b82f6;
}

.card-icon--dpo {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.card-icon--dpo svg {
  width: 24px;
  height: 24px;
}

.card-price {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.price-discounted {
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
}

.price-original {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: line-through;
}

.card-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #f4f4f5;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.card-icon img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.card-content {
  flex: 1;
  min-width: 0;
}

.card-title {
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #18181b;
  margin-bottom: 0.125rem;
}

.card-description {
  display: block;
  font-size: 0.8125rem;
  color: #71717a;
  line-height: 1.4;
}

.card-arrow {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: #a1a1aa;
  transition: transform 0.2s, color 0.2s;
}

.payment-card:hover:not(:disabled) .card-arrow {
  transform: translateX(4px);
  color: #18181b;
}

.card-arrow svg {
  width: 100%;
  height: 100%;
}

.card-loading {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e4e4e7;
  border-top-color: #18181b;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 576px) {
  .payment-card {
    padding: 0.875rem 1rem;
  }

  .card-icon {
    width: 40px;
    height: 40px;
  }

  .card-icon img {
    width: 28px;
    height: 28px;
  }

  .card-title {
    font-size: 0.875rem;
  }

  .card-description {
    font-size: 0.75rem;
  }
}
</style>
